<template>
  <f7-toolbar top class="sub-menu">
    <div class="sub-menu__inner">
      <f7-link
        v-for="(item, idx) in filteredItems"
        :key="`sub-item-${idx}`"
        class="sub-menu__link"
        :class="{ 'is-active': isActiveItem(item) }"
        :href="item.url || undefined"
        @click="onItemClick(item, $event)"
      >
        <span class="sub-menu__text">{{ displayText(item) }}</span>
      </f7-link>
    </div>
  </f7-toolbar>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { IS_LOCAL_TEST } from '@/js/constants';
import {
  hasMySmartAnalyticsAccess,
  hasNutrivisorAccess,
  blockedRoutePatterns,
  matchesRoutePattern,
} from '@/js/routeAccess';

export default {
  name: 'SubMenu',
  props: {
    items: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  data() {
    return {
      curPath: '/',
    };
  },
  computed: {
    ...mapState('contact', [
      'hasAccessToAdvanceService',
      'hasAccessToNutritionalMonitoring',
      'enabled',
      'company',
    ]),
    ...mapState('PowerBiModules', ['availableCountries']),
    ...mapGetters('user', ['isAdmin']),

    currentPath() {
      return this.curPath || '/';
    },

    filteredItems() {
      // El submenú reutiliza la misma lógica que el control de rutas
      // para evitar diferencias entre "lo veo" y "puedo entrar".
      const canAccessMySmartAnalytics = hasMySmartAnalyticsAccess(
        this.availableCountries,
        this.company
      );
      const canAccessNutrivisor = hasNutrivisorAccess({
        hasAccessToAdvanceService: this.hasAccessToAdvanceService,
        hasAccessToNutritionalMonitoring:
          this.hasAccessToNutritionalMonitoring,
      });

      return this.items.filter((item) => {
        // Normalizamos aquí porque menuOptions mezcla rutas con y sin "/"
        // final, y así la comparación responde bien al cambiar de cliente.
        const normalizedItemUrl = this.normalizePath(item?.url);

        if (
          blockedRoutePatterns.some((pattern) =>
            matchesRoutePattern(normalizedItemUrl, pattern)
          )
        ) {
          return false;
        }

        if (
          normalizedItemUrl === '/agronomy/advanced-services' &&
          this.enabled
        ) {
          return false;
        }
        if (
          normalizedItemUrl === '/agronomy/nutritionalMonitoring' &&
          !canAccessNutrivisor
        ) {
          return false;
        }
        if (
          normalizedItemUrl === '/food/StatsQueries' &&
          !canAccessMySmartAnalytics
        ) {
          return false;
        }
        if (
          normalizedItemUrl === '/agronomy/NutriVisor' &&
          !canAccessNutrivisor
        ) {
          return false;
        }
        if (
          (normalizedItemUrl === '/client-area' ||
            normalizedItemUrl ===
              '/client-area/contact-management') &&
          !this.isAdmin
        ) {
          return false;
        }
        return true;
      });
    },

    // URL del ítem de submenú que mejor encaja con la ruta actual
    activeItemUrl() {
      const cur = this.normalizePath(this.currentPath);
      let best = null;
      let bestLen = -1;
      this.filteredItems.forEach((item) => {
        if (!item || !item.url) return;
        const target = this.normalizePath(item.url);

        // Acepto coincidencia exacta o el prefijo con subrutas
        const matches =
          cur === target || cur.startsWith(`${target}/`);

        if (matches && target.length > bestLen) {
          best = target;
          bestLen = target.length;
        }
      });

      return best;
    },
  },
  beforeMount() {
    this.fetchCountriesAvailables();
  },
  mounted() {
    // Inicializo el path actual leyendo la ruta real del router
    this.curPath = this.safeGetCurrentPath();

    this.$f7ready(() => {
      const view = this.$f7?.views?.main;

      // Función que gestiona los cambios de ruta y evita que el calendario
      // o los modales alteren la detección del path activo
      const handleRouteChangeSub = (to) => {
        // Obtengo siempre la ruta real desde el router principal
        const realPath =
          this.$f7?.views?.main?.router?.currentRoute?.path || '/';
        const normalizedReal = this.normalizePath(realPath);

        // Normalizo la ruta que Framework7 indica como "destino"
        const incoming = this.normalizePath(to?.path || '/');

        // Si lo que ha cambiado es un modal (como el calendario), lo ignoro
        if (to?.route?.modal || to?.modal) return;

        // Compruebo si sigue abierto un calendario o un picker, y si es así,
        // evito actualizar la ruta para no romper el menú
        const isCalendarAlive = document.querySelector(
          '.calendar-modal, .picker-modal'
        );
        if (isCalendarAlive) return;

        // Evito que Framework7 trate un cierre del calendario como si
        // estuviera navegando a '/', ya que no es una navegación real
        if (incoming === '/' && normalizedReal !== '/') return;

        // Actualizo realmente el path interno cuando la navegación es válida
        this.curPath = normalizedReal;
      };

      // Suscribo el handler a los cambios de ruta del router principal
      view?.router?.on?.('routeChange', handleRouteChangeSub);
    });
  },
  beforeDestroy() {
    const view = this.$f7?.views?.main;
    view?.router?.off?.('routeChange');
  },
  methods: {
    safeGetCurrentPath() {
      try {
        return this.normalizePath(
          this.$f7?.views?.main?.router?.currentRoute?.path || '/'
        );
      } catch {
        return '/';
      }
    },
    normalizePath(p) {
      if (!p) return '/';
      let s = String(p).trim();
      if (s.length > 1 && s.endsWith('/')) s = s.slice(0, -1);
      return s;
    },
    displayText(item) {
      const key = (item && (item.name || item.title)) || '';
      return this.$t ? this.$t(key) : key;
    },
    isActiveItem(item) {
      if (!item || !item.url) return false;

      const target = this.normalizePath(item.url);

      // Solo se marca activo el ítem cuyo target coincide con el "mejor" target encontrado
      return target === this.activeItemUrl;
    },
    onItemClick(item, evt) {
      try {
        const label = `${this.displayText(item)} | ${
          item?.url || ''
        }`.trim();
        // Si estamos en  entorno local de pruebas, no se registra el evento
        if (!IS_LOCAL_TEST) {
          window.gtag?.('event', 'Click', {
            event_category: 'SubMenu',
            event_label: label,
          });
        }
      } catch (e) {
        console.error('Error al registrar click en SubMenu', e);
      }

      if (!item || !item.url) {
        evt?.preventDefault?.();
        return;
      }

      const normalized = this.normalizePath(item.url);
      this.curPath = normalized;

      const view = this.$f7?.views?.main;
      view?.router?.navigate(normalized, {
        animate: true,
        transition: 'f7-fade',
      });
    },
    ...mapActions('PowerBiModules', ['fetchCountriesAvailables']),
  },
};
</script>

<style lang="scss" scoped>
@import './SubMenu.styles.scss';
</style>
